import useScrollReveal from "@/hooks/useScrollReveal";

interface Testimonial {
  quote: string;
  author: string;
  location: string;
  vehicle: string;
}

interface TestimonialsSectionProps {
  tagline: string;
  title: string;
  testimonials: Testimonial[];
  ctaText: string;
  ctaHref: string;
  backgroundImage?: string;
}

export default function TestimonialsSection({
  tagline,
  title,
  testimonials,
  ctaText,
  ctaHref,
  backgroundImage,
}: TestimonialsSectionProps) {
  const headRef = useScrollReveal<HTMLDivElement>();
  const featRef = useScrollReveal<HTMLDivElement>();
  const gridRef = useScrollReveal<HTMLDivElement>();

  const featured = testimonials[0];
  const rest = testimonials.slice(1);

  return (
    <section className="relative overflow-hidden">
      {backgroundImage && (
        <>
          <div
            className="absolute inset-0 bg-cover bg-center bg-fixed"
            style={{ backgroundImage: `url('${backgroundImage}')` }}
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal/97 via-charcoal/93 to-charcoal/98" />
        </>
      )}
      {!backgroundImage && <div className="absolute inset-0 bg-charcoal" />}

      <div className="grain-overlay absolute inset-0 pointer-events-none" aria-hidden="true" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/20 to-transparent" />

      {/* Enormous atmospheric quote mark */}
      <div
        className="absolute left-0 top-0 font-display text-white select-none pointer-events-none"
        style={{ fontSize: "700px", lineHeight: 1, opacity: 0.012, transform: "translate(-120px, -180px)" }}
        aria-hidden="true"
      >&ldquo;</div>

      <div className="relative z-10 px-6 lg:px-14" style={{ paddingTop: "clamp(80px, 10vw, 160px)", paddingBottom: "clamp(80px, 10vw, 160px)" }}>
        <div className="max-w-7xl mx-auto">

          {/* Header */}
          <div ref={headRef} className="sr-hidden text-center mb-32">
            <div className="flex items-center justify-center gap-5 mb-7">
              <span className="inline-block w-16 h-px bg-gradient-to-r from-transparent via-gold to-transparent" />
              <p className="text-gold font-sans text-[11px] font-bold uppercase tracking-[0.34em]">{tagline}</p>
              <span className="inline-block w-16 h-px bg-gradient-to-r from-transparent via-gold to-transparent" />
            </div>
            <h2
              className="font-display text-white font-bold leading-[0.90]"
              style={{ fontSize: "clamp(44px, 7vw, 96px)" }}
            >
              {title}
            </h2>
          </div>

          {/* Featured testimonial — massive editorial */}
          {featured && (
            <div ref={featRef} className="sr-hidden mb-10">
              <article className="relative overflow-hidden">
                {/* Top gold accent */}
                <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-gold via-gold/50 to-transparent" />

                {/* Large decorative quote */}
                <div
                  className="absolute top-0 right-8 font-display text-gold/8 font-bold leading-none select-none pointer-events-none"
                  style={{ fontSize: "280px", lineHeight: 1 }}
                  aria-hidden="true"
                >&ldquo;</div>

                <div className="relative z-10 py-20 lg:py-28 px-0 lg:px-8">
                  {/* Stars */}
                  <div className="flex gap-2 mb-12">
                    {Array.from({ length: 5 }).map((_, j) => (
                      <div key={j} className="w-5 h-5 flex items-center justify-center text-gold">
                        <i className="ri-star-fill text-sm"></i>
                      </div>
                    ))}
                  </div>

                  {/* The quote — editorial scale */}
                  <blockquote
                    className="font-display text-white italic leading-[1.28] mb-16 max-w-5xl"
                    style={{ fontSize: "clamp(26px, 4vw, 52px)" }}
                  >
                    &ldquo;{featured.quote}&rdquo;
                  </blockquote>

                  {/* Author */}
                  <div className="flex items-center gap-8">
                    <div className="w-20 h-px bg-gold flex-shrink-0" />
                    <div>
                      <p className="text-white font-sans text-base font-bold tracking-wide">{featured.author}</p>
                      <p className="text-gold font-sans text-sm font-semibold mt-1 italic">{featured.vehicle}</p>
                      <p className="text-stone-500 font-sans text-xs mt-1">{featured.location}</p>
                    </div>
                  </div>
                </div>

                {/* Bottom hairline */}
                <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-stone-800/60 via-stone-700/40 to-transparent" />
              </article>
            </div>
          )}

          {/* Secondary testimonials — open, airy, not boxed */}
          {rest.length > 0 && (
            <div ref={gridRef} className="sr-hidden sr-delay-2 grid grid-cols-1 md:grid-cols-2 gap-px bg-stone-800/30 mb-28">
              {rest.map((t, i) => (
                <article
                  key={i}
                  className="group relative bg-charcoal/60 backdrop-blur-sm py-16 px-10 lg:px-14 flex flex-col overflow-hidden hover:bg-white/[0.03] transition-all duration-500"
                >
                  {/* Stars */}
                  <div className="flex gap-1.5 mb-8">
                    {Array.from({ length: 5 }).map((_, j) => (
                      <div key={j} className="w-4 h-4 flex items-center justify-center text-gold">
                        <i className="ri-star-fill text-xs"></i>
                      </div>
                    ))}
                  </div>

                  {/* Quote */}
                  <blockquote
                    className="font-display text-stone-200 italic leading-[1.4] flex-1 mb-10"
                    style={{ fontSize: "clamp(20px, 2.2vw, 28px)" }}
                  >
                    &ldquo;{t.quote}&rdquo;
                  </blockquote>

                  {/* Author */}
                  <div className="flex items-center gap-5">
                    <div className="w-10 h-px bg-gold/60 flex-shrink-0" />
                    <div>
                      <p className="text-white font-sans text-sm font-bold">{t.author}</p>
                      <p className="text-gold font-sans text-xs font-semibold mt-0.5 italic">{t.vehicle}</p>
                      <p className="text-stone-600 font-sans text-xs mt-0.5">{t.location}</p>
                    </div>
                  </div>

                  {/* Bottom gold sweep on hover */}
                  <div className="absolute bottom-0 left-0 w-0 h-[2px] bg-gradient-to-r from-gold to-gold/30 group-hover:w-full transition-all duration-700 ease-out" />
                </article>
              ))}
            </div>
          )}

          {/* CTA */}
          <div className="text-center">
            <a
              href={ctaHref}
              target="_blank"
              rel="nofollow noreferrer"
              className="group inline-flex items-center gap-3 px-14 py-5 bg-gold text-white font-sans font-bold text-[11px] uppercase tracking-[0.24em] hover:bg-gold-dark transition-all whitespace-nowrap cursor-pointer"
              style={{ boxShadow: "0 10px 48px rgba(201,168,76,0.28)" }}
            >
              {ctaText}
              <span className="w-4 h-4 flex items-center justify-center group-hover:translate-x-0.5 transition-transform">
                <i className="ri-external-link-line"></i>
              </span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
